import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';

const AGBPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, getLegalVariables } = useLanguage();

  // Get dynamic variables for legal content
  const legalVars = getLegalVariables();

  const handleBack = () => {
    // Check if user came from within the app
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      // Default to landing page
      navigate('/');
    }
  };

  // Helper function to render content with line breaks and markdown formatting
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      // Skip empty lines but add spacing
      if (line.trim() === '') {
        return <Box key={index} sx={{ mb: 2 }} />;
      }

      // Handle bullet points
      if (line.trim().startsWith('•')) {
        return (
          <Typography 
            key={index} 
            variant="body1" 
            sx={{ 
              mb: 0.5, 
              ml: 2, 
              position: 'relative',
              '&::before': {
                content: '"•"',
                position: 'absolute',
                left: -16,
                color: 'primary.main'
              }
            }}
          >
            {renderFormattedText(line.replace(/^•\s*/, ''))}
          </Typography>
        );
      }

      // Handle numbered lists
      if (/^\d+\.\s/.test(line.trim())) {
        return (
          <Typography 
            key={index} 
            variant="body1" 
            sx={{ mb: 0.5, ml: 2 }}
          >
            {renderFormattedText(line)}
          </Typography>
        );
      }

      // Regular paragraphs
      return (
        <Typography key={index} variant="body1" sx={{ mb: 1 }}>
          {renderFormattedText(line)}
        </Typography>
      );
    });
  };

  // Helper function to render formatted text with bold, links, etc.
  const renderFormattedText = (text) => {
    if (!text) return '';
    
    let processedText = text;
    
    // First, extract and replace bold text with placeholders
    const boldMatches = [];
    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;
    
    while ((match = boldRegex.exec(text)) !== null) {
      const placeholder = `__BOLD_${boldMatches.length}__`;
      boldMatches.push(match[1]);
      processedText = processedText.replace(match[0], placeholder);
    }
    
    // Then split by spaces and process each word
    const words = processedText.split(' ');
    
    return words.map((word, index) => {
      // Handle bold placeholders
      const boldMatch = word.match(/__BOLD_(\d+)__/);
      if (boldMatch) {
        const boldIndex = parseInt(boldMatch[1]);
        return (
          <strong key={`bold-${index}`} style={{ fontWeight: 600 }}>
            {boldMatches[boldIndex]}
          </strong>
        );
      }
      
      // Handle URLs
      const urlRegex = /^(https?:\/\/[^\s]+)$/;
      if (urlRegex.test(word)) {
        return (
          <a 
            key={`link-${index}`}
            href={word}
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: tokens.colors.primary.main,
              textDecoration: 'underline'
            }}
          >
            {word}
          </a>
        );
      }
      
      // Regular word with space (except for last word)
      return index < words.length - 1 ? `${word} ` : word;
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: tokens.colors.background.default,
        py: 4
      }}
    >
      <Container maxWidth="md">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{
            mb: 4,
            color: tokens.colors.primary.main,
            textTransform: 'none',
            fontWeight: tokens.typography.fontWeights.medium,
            '&:hover': {
              backgroundColor: `${tokens.colors.primary.main}10`
            }
          }}
        >
          {t('legal.common.backButton')}
        </Button>

        {/* Page Content */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: tokens.radius.xl,
            border: `1px solid ${tokens.colors.borders.light}`
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: 4
            }}
          >
            {t('legal.terms.title')}
          </Typography>

          {/* 1. Scope */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.scope.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.scope.content', legalVars)}
            </Typography>
          </Box>

          {/* 2. Service Description */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.serviceDescription.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.serviceDescription.content', legalVars)}
            </Typography>
          </Box>

          {/* 3. Rights and Pricing */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.rightsAndPricing.title')}
            </Typography>
            {renderContent(t('legal.terms.sections.rightsAndPricing.content', legalVars))}
          </Box>

          {/* 4. Payment Terms */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.paymentTerms.title')}
            </Typography>
            {renderContent(t('legal.terms.sections.paymentTerms.content', legalVars))}
          </Box>

          {/* 5. User Obligations */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.userObligations.title')}
            </Typography>
            {renderContent(t('legal.terms.sections.userObligations.content'))}
          </Box>

          {/* 6. Data Protection */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.dataProtection.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.dataProtection.content')}
            </Typography>
          </Box>

          {/* 7. Service Availability */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.availability.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.availability.content')}
            </Typography>
          </Box>

          {/* 8. Liability */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.liability.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.liability.content')}
            </Typography>
          </Box>

          {/* 9. Copyright */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.copyright.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.copyright.content')}
            </Typography>
          </Box>

          {/* 10. Changes */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.changes.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.changes.content')}
            </Typography>
          </Box>

          {/* 11. Final Provisions */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: tokens.typography.fontWeights.semibold,
                color: tokens.colors.text.primary,
                mb: 2
              }}
            >
              {t('legal.terms.sections.finalProvisions.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('legal.terms.sections.finalProvisions.content')}
            </Typography>
          </Box>

          {/* Last Updated */}
          <Box sx={{ 
            pt: 3, 
            borderTop: `1px solid ${tokens.colors.borders.light}`,
            textAlign: 'center'
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: tokens.colors.text.secondary,
                fontStyle: 'italic'
              }}
            >
              {t('legal.common.lastUpdated', legalVars)}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AGBPage; 